import type { CandidateBrowseRow } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import type { Column } from '../../../../components/data-table/types';
import { formatDateTime } from '../../../../lib/format';
import { experienceLabel } from '../../candidate-format';
import { applicationsText, titleAndCity } from './list-format';
import { NameLink, OverBudgetChip, PersonFlags, Score, StageChip, StarButton, TagList, stageText, type RowHandlers } from './RowBits';
import styles from './CandidateTable.module.css';

/** The column ids, also the column chooser's and the saved views' (mockup Main.dc.html's order). */
export const ACTIVITY_COLUMN = 'activity';

/**
 * CAND-01's columns. `exportValue` is each cell as text for "Export selected" — built from what
 * the server sent, so a masked field exports as it shows (#5).
 */
export function candidateColumns(handlers: RowHandlers): readonly Column<CandidateBrowseRow>[] {
  return [
    {
      id: 'star',
      header: 'Star',
      cell: (row) => <StarButton row={row} handlers={handlers} />,
      exportValue: (row) => (row.isStarred ? 'Starred' : ''),
    },
    {
      id: 'candidate',
      header: 'Candidate',
      isIdentity: true,
      cell: (row) => (
        <span className={styles.who}>
          <Avatar name={row.candidate.fullName} />
          <span className={styles.whoText}>
            <NameLink row={row} handlers={handlers} />
            {titleAndCity(row) !== null && <span className={styles.sub}>{titleAndCity(row)}</span>}
            <PersonFlags row={row} />
          </span>
        </span>
      ),
      exportValue: (row) => [row.candidate.fullName, titleAndCity(row)].filter((part) => part !== null).join(' · '),
    },
    {
      id: 'applied',
      header: 'Applied for',
      cell: (row) =>
        row.focus === null ? null : (
          <span className={styles.stack}>
            <span className={styles.job}>{row.focus.jobTitle}</span>
            <span className={styles.subNumber}>{applicationsText(row.liveApplications, row.totalApplications)}</span>
            <OverBudgetChip row={row} />
          </span>
        ),
      exportValue: (row) => (row.focus === null ? null : `${row.focus.jobTitle} · ${applicationsText(row.liveApplications, row.totalApplications)}`),
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: (row) =>
        row.focus === null ? null : (
          <span className={styles.stack}>
            <StageChip row={row} handlers={handlers} />
            {row.focus.stepDetail !== null && <span className={styles.sub}>{row.focus.stepDetail}</span>}
          </span>
        ),
      exportValue: (row) => stageText(row, handlers),
    },
    {
      id: 'score',
      header: 'Match',
      cell: (row) => ((row.focus?.matchScore ?? row.focus?.stars ?? null) === null ? null : <Score row={row} />),
      exportValue: (row) => row.focus?.matchScore ?? null,
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: (row) => (row.tags.length === 0 ? null : <TagList tags={row.tags} max={3} />),
      exportValue: (row) => row.tags.map((tag) => tag.name).join(', '),
    },
    {
      id: 'experience',
      header: 'Experience',
      cell: (row) => experienceLabel(row.candidate.totalExperienceMonths),
    },
    {
      id: ACTIVITY_COLUMN,
      header: 'Last activity',
      // Drawn by the table's last cell, which shares its place with the hover actions.
      cell: (row) => (row.lastActivityAt === null ? null : formatDateTime(row.lastActivityAt)),
    },
  ];
}
